import React from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useState } from "react";
import { useRef } from "react";
import { useCallback } from "react";
import { classNames } from "./../../../../helpers/classNames";
import { ChevronDownIcon, CheckIcon, SearchIcon } from "@heroicons/react/solid";

const SingleSelect = ({
  rowId,
  initValue,
  accessToken,
  table,
  colId,
  editable,
  selectOptions,
  editId,
  onCellChange,
  isEditing = false,
  startEditing,
  stopEditing
}) => {
  const [selected, setSelected] = useState(initValue || "");
  const [bgColor, setBgColor] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef([]);

  const sortedOptions = useMemo(
    () => selectOptions?.sort((a, b) => a.order - b.order) || [],
    [selectOptions]
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return sortedOptions;
    return sortedOptions.filter(option =>
      option.value.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [sortedOptions, searchQuery]);

  const submitChanges = useCallback(async (newValue) => {
    if (!newValue) return;

    const oldSelected = selected;

    try {
      // Optimistic update
      setSelected(newValue);

      // Trigger cell update acknowledgment
      if (onCellChange) {
        onCellChange(rowId, colId, newValue);
      }

      // Close dropdown and reset search
      setIsOpen(false);
      setHighlightedIndex(-1);
      setSearchQuery("");

      // Stop editing - this will be handled by the parent component
      if (stopEditing) {
        stopEditing();
      }
    } catch (error) {
      console.error("Error updating single select:", error);
      // Revert on error
      setSelected(oldSelected);
    }
  }, [selected, onCellChange, rowId, colId, stopEditing]);

  const handleOptionClick = useCallback((optionValue) => {
    submitChanges(optionValue);
  }, [submitChanges]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(0);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case ' ':
        // Allow space in search input
        if (document.activeElement === searchInputRef.current) {
          return;
        }
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchQuery("");
        buttonRef.current?.focus();
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchQuery("");
        if (stopEditing) {
          stopEditing();
        }
        break;
      default:
        break;
    }
  }, [isOpen, highlightedIndex, filteredOptions, handleOptionClick, stopEditing]);

  useEffect(() => {
    if (!selected || !selectOptions) return;
    setBgColor(
      selectOptions.find((option) => option.value === selected)?.color
    );
  }, [selectOptions, selected]);

  useEffect(() => {
    setSelected(initValue || "");
  }, [initValue]);

  // Auto-open dropdown when isEditing becomes true
  useEffect(() => {
    if (isEditing && !isOpen) {
      setIsOpen(true);
      setHighlightedIndex(0);
      // Focus search input after dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isEditing, isOpen]);

  // Reset highlighted index when search query changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchQuery("");
        if (isEditing && stopEditing) {
          stopEditing();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isEditing, stopEditing]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  return (
    <>
      {editable ? (
        <>
          {isEditing ? (
            <div ref={dropdownRef} className="relative w-full">
              <button
                ref={buttonRef}
                type="button"
                onClick={() => {
                  setIsOpen(!isOpen);
                  if (!isOpen) {
                    setSearchQuery("");
                    setTimeout(() => searchInputRef.current?.focus(), 50);
                  }
                }}
                onKeyDown={handleKeyDown}
                className="relative w-full bg-white border-2 border-blue-500 rounded-md shadow-sm pl-3 pr-10 py-1 text-left cursor-pointer focus:outline-none focus:ring-0 sm:text-sm"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
              >
                <span className="block truncate">
                  {selected || "Select an option"}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </button>

              {isOpen && (
                <div className="absolute z-10 mt-1 left-0 min-w-[250px] max-w-[400px] w-max bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {/* Search Input */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent dropdown keyboard shortcuts when typing in search
                          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                            handleKeyDown(e);
                          }
                        }}
                        placeholder="Search options..."
                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Options */}
                  <div className="max-h-60 overflow-auto py-1">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No options found
                      </div>
                    ) : (
                      filteredOptions.map((option, index) => (
                        <div
                          key={option.order}
                          ref={el => optionsRef.current[index] = el}
                          onClick={() => handleOptionClick(option.value)}
                          className={classNames(
                            "cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50",
                            highlightedIndex === index ? "bg-blue-50" : "",
                            selected === option.value ? "font-semibold" : "font-normal"
                          )}
                          role="option"
                          aria-selected={selected === option.value}
                        >
                          <div className="flex items-center">
                            <span
                              className={classNames(
                                "w-3 h-3 rounded-full mr-2",
                                option.color || ""
                              )}
                            />
                            <span className="block truncate">
                              {option.value}
                            </span>
                          </div>
                          {selected === option.value && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              onClick={startEditing}
              className={classNames(
                "group cursor-pointer border-2 border-transparent hover:border-blue-500 rounded-md transition-all duration-150 ease-in-out h-full flex items-center w-full",
                selected ? "px-0" : "px-2 py-1"
              )}
            >
              {selected ? (
                <div
                  className={classNames(
                    bgColor,
                    "rounded-2xl px-2 py-1 text-center w-full h-full flex items-center justify-center"
                  )}
                >
                  <p className="text-center text-white truncate">{selected}</p>
                </div>
              ) : (
                <p className="text-gray-400"></p>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {selected ? (
            <div
              className={classNames(
                bgColor,
                "rounded-2xl px-2 py-1 text-center w-full h-full flex items-center justify-center"
              )}
            >
              <p className="text-center text-white truncate">{selected}</p>
            </div>
          ) : (
            <p className="text-gray-400 px-2 py-1">-</p>
          )}
        </>
      )}
    </>
  );
};

export default SingleSelect;
